import type { SignalInboxBacklog } from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";

/** "Clear", "<1 h", "5 h", "3 d" — or "Growing" when more comes in than goes out. */
export function drainLabel(pending: number, hoursToDrain: number | null): { text: string; growing: boolean } {
  if (pending === 0) return { text: "Clear", growing: false };
  const h = hoursToDrain;
  if (h == null) return { text: "Growing", growing: true };
  if (h < 1) return { text: "<1 h", growing: false };
  if (h < 48) return { text: `${Math.round(h)} h`, growing: false };
  return { text: `${Math.round(h / 24)} d`, growing: false };
}

/** A latency: "1.5 s", "42 s", or "–" when unknown. */
export function seconds(ms: number | null): string {
  if (ms == null) return "–";
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 1000)} s`;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

/** A per-hour count as a rate per second: "0", "<0.1", "3.8", "42". */
export function perSecond(perHour: number): string {
  const r = perHour / 3600;
  if (r === 0) return "0";
  if (r < 0.1) return "<0.1";
  return r < 10 ? r.toFixed(1) : formatNumber(Math.round(r));
}

/**
 * Evaluations per hour the engine needs to keep up with a data type: the records
 * coming in times the evaluations a finished record took in the last hour (a
 * record reaches the workflows of the tenants its source picks, not always all
 * of them); before anything finished, one per workflow watching the type.
 */
export function evaluationsNeeded(q: SignalInboxBacklog): number {
  const finished = q.processed_1h ?? 0;
  const perRecord = finished > 0 ? q.evaluations_1h / finished : q.active_workflows;
  return (q.added_1h ?? 0) * perRecord;
}

/** "950", "34K", "1.2M" in the browser's locale. */
export function compactNumber(n: number): string {
  return n.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

/** Model time: "42 s", "12 min", "3.4 h", or "–" when unknown. */
export function duration(ms: number | null): string {
  if (ms == null) return "–";
  const s = ms / 1000;
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}
